import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { api, errMsg, tierRank } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export const DAYS = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
export const isDay = (h) => DAYS.some((d) => String(h || "").toLowerCase().startsWith(d.toLowerCase()));
export const todayIndex = (sections = []) => {
  const t = DAYS[new Date().getDay()].toLowerCase();
  return sections.findIndex((s) => String(s.heading || "").toLowerCase().startsWith(t));
};
export const key = (s, i) => `${s}:${i}`;

export const usePaidPlan = () => {
  const { tier, billing } = useAuth();
  return tierRank(tier) >= 1 || !!billing?.trial_active;
};

export const askUpgrade = () => window.dispatchEvent(new CustomEvent("rpopro:upgrade", { detail: { tier: "pro", message: "Weekly plan check-offs, streaks and the morning plan email are part of Pro." } }));
const isLocked = (e) => [402, 403].includes(e?.response?.status) && (e.response.data?.detail?.code === "upgrade_required" || e.response.status === 402);

const recount = (progress, sections, done) => {
  const total = progress?.total ?? sections.reduce((n, s) => n + (s.items?.length || 0), 0);
  return { ...progress, done, done_count: done.length, total, pct: total ? Math.round((done.length / total) * 100) : 0 };
};

const celebrate = (prev = {}, next = {}) => {
  if ((next.current || 0) > (prev.current || 0)) toast.success(`Day complete! ${next.current}-day streak`, { id: "plan-streak" });
  if (next.week_complete && !prev.week_complete) toast.success("Perfect week!", { id: "plan-perfect" });
};

export const useWeekPlan = (enabled = true) => {
  const [plan, setPlan] = useState(undefined);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get("/calendar").then((r) => setPlan(r.data?.plan || null)).catch((e) => { setPlan(null); if (isLocked(e)) return; toast.error(errMsg(e)); }), []);
  useEffect(() => { if (enabled) load(); else setPlan(null); }, [enabled, load]);

  const toggle = async (s, i, done) => {
    const before = plan;
    const sections = plan.result?.sections || [];
    const set = new Set(plan.progress?.done || []);
    done ? set.add(key(s, i)) : set.delete(key(s, i));
    setPlan({ ...plan, progress: recount(plan.progress, sections, [...set]) });
    try {
      const { data } = await api.put("/calendar/plan/check", { plan_at: plan.created_at, section: s, item: i, done });
      setPlan((p) => ({ ...p, progress: { ...p.progress, ...data } }));
      celebrate(before.progress?.streak, data.streak);
    } catch (e) {
      setPlan(before);
      if (e.response?.status === 409) { toast.message(errMsg(e)); load(); }
      else if (isLocked(e)) askUpgrade();
      else if (e.response?.status === 404 && !plan.progress) toast.message("Check-offs are being switched on. Please try again soon.");
      else toast.error(errMsg(e));
    }
  };

  const replan = async () => {
    setBusy(true);
    try { await api.post("/ai/tools/weekly_plan", { job_id: null, inputs: {} }); await load(); toast.success("Your week is planned"); }
    catch (e) { if (isLocked(e)) askUpgrade(); else toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  return { plan, busy, toggle, replan, reload: load };
};
